import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/apiClient";
import {Device, DeviceType} from "@/types/devices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ChevronDown, ChevronUp, Save } from "lucide-react";
import {Route} from "@/types/routes";

const AdminPanel: React.FC = () => {
    const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
    const [devices, setDevices] = useState<Device[]>([]);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [typeNameEdits, setTypeNameEdits] = useState<Record<string, string>>({});
    const [deviceNameEdits, setDeviceNameEdits] = useState<Record<string, string>>({});
    const [deviceColorEdits, setDeviceColorEdits] = useState<Record<string, string>>({});
    const [deviceIconEdits, setDeviceIconEdits] = useState<Record<string, string>>({});

    useEffect(() => {
        fetchDeviceTypes();
        fetchDevices();
        fetchActiveRoutes();
    }, []);

    const [activeRoutes, setActiveRoutes] = useState<Route[]>([]);

    const fetchActiveRoutes = async () => {
        try {
            const data = await apiClient.getActiveRoutes();
            setActiveRoutes(data);
        } catch (error) {
            console.error("Ошибка загрузки маршрутов", error);
        }
    };



    const fetchDeviceTypes = async () => {
        try {
            const data = await apiClient.getDeviceTypes();
            setDeviceTypes(data.sort((a, b) => a.id.localeCompare(b.id)));
        } catch (error) {
            console.error("Ошибка загрузки типов устройств", error);
        }
    };

    const fetchDevices = async () => {
        try {
            const data = await apiClient.getDevices();
            setDevices(data.sort((a, b) => a.id.localeCompare(b.id)));
        } catch (error) {
            console.error("Ошибка загрузки устройств", error);
        }
    };

    const togglePublicAccess = async (id: string, isEnabled: boolean) => {
        try {
            await apiClient.togglePublicAccess(id, isEnabled);
            fetchDeviceTypes();
        } catch (error) {
            console.error("Ошибка обновления доступа", error);
        }
    };

    const updateDeviceTypeName = async (id: string) => {
        const name = typeNameEdits[id];
        if (name) {
            try {
                await apiClient.updateDeviceTypeName(id, name);
                fetchDeviceTypes();
            } catch (error) {
                console.error("Ошибка обновления имени типа устройства", error);
            }
        }
    };

    const updateDeviceName = async (id: string) => {
        const name = deviceNameEdits[id] || "";
        try {
            await apiClient.updateDeviceName(id, name);
            fetchDevices();
        } catch (error) {
            console.error("Ошибка обновления имени устройства", error);
        }
    };

    const updateDeviceIcon = async (id: string) => {
        const icon = deviceIconEdits[id] || "";

            try {
                await apiClient.updateDeviceIcon(id, icon);
                fetchDevices();
            } catch (error) {
                console.error("Ошибка обновления иконки устройства", error);
            }
    };

    const updateDeviceColor = async (id: string) => {
        const color = deviceColorEdits[id] || "";

        try {
            await apiClient.updateDeviceColor(id, color);
            fetchDevices();
        } catch (error) {
            console.error("Ошибка обновления цвета трека", error);
        }

    };

    const toggleDevice = async (id: string, enabled: boolean) => {
        try {
            await apiClient.toggleDevice(id, enabled);
            fetchDevices();
        } catch (error) {
            console.error("Ошибка изменения статуса устройства", error);
        }
    };

    const getLastPacketDate = (typeId: string): string => {
        const typeDevices = devices.filter((device) => device.typeId === typeId);
        if (typeDevices.length === 0) return "0000-00-00 00:00:00";
        return typeDevices.reduce((latest, device) => {
            return device.last_package_at > latest ? device.last_package_at : latest;
        }, "0000-00-00 00:00:00");
    };

    return (
        <div className="p-4 space-y-4">
            <h1 className="text-xl font-bold mb-4">Устройства</h1>
            <table className="w-full border-collapse border border-gray-300">
                <thead>
                <tr className="bg-gray-200">
                    <th className="border p-2">ID</th>
                    <th className="border p-2">Доступно публично</th>
                    <th className="border p-2">Тип</th>
                    <th className="border p-2">Последний пакет</th>
                    <th className="border p-2">Устройства</th>
                </tr>
                </thead>
                <tbody>
                {deviceTypes.map((type) => (
                    <>
                        <tr key={type.id} className="border">
                            <td className="border p-2 text-center">{type.id}</td>
                            <td className="border p-2 text-center">
                                <Switch
                                    checked={type.user_role === "public"}
                                    onCheckedChange={(checked) => togglePublicAccess(type.id, checked)}
                                    className="w-8 h-5"
                                />
                            </td>
                            <td className="border p-2 flex items-center space-x-2">
                                <Input
                                    defaultValue={type.name}
                                    maxLength={30}
                                    onChange={(e) =>
                                        setTypeNameEdits({...typeNameEdits, [type.id]: e.target.value})
                                    }
                                    className="w-full max-w-[300px]"
                                />
                                <Button size="icon" onClick={() => updateDeviceTypeName(type.id)}>
                                    <Save size={16}/>
                                </Button>
                            </td>
                            <td className="border p-2 text-center">{getLastPacketDate(type.id) || "Нет данных"}</td>
                            <td className="border p-2 text-center">
                                <Button
                                    size="icon"
                                    onClick={() => setExpanded(expanded === type.id ? null : type.id)}
                                >
                                    {expanded === type.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                                </Button>
                                <span
                                    className="ml-2 text-sm text-gray-600">({devices.filter((device) => device.typeId === type.id).length})</span>
                            </td>
                        </tr>
                        {expanded === type.id && (
                            <tr>
                                <td colSpan={5} className="p-2 border bg-gray-100">
                                    {devices.filter((device) => device.typeId === type.id).map((device) => (
                                        <div
                                            key={device.id}
                                            className="flex justify-between items-center border p-2 rounded-md bg-white mb-1"
                                        >
                                            <span className="w-1/6 text-center">{device.id}</span>
                                            <select
                                                value={device.route_id || ""}
                                                onChange={async (e) => {
                                                    const newRouteId = e.target.value;
                                                    await apiClient.setDeviceRoute(device.id, newRouteId);
                                                    setDevices((prevDevices) =>
                                                        prevDevices.map((d) =>
                                                            d.id === device.id ? { ...d, route_id: newRouteId } : d
                                                        )
                                                    );
                                                }}

                                                className="border p-1 rounded"
                                            >
                                                <option value="">-- Маршрут не выбран --</option>
                                                {activeRoutes.map((route) => (
                                                    <option key={route.id} value={route.id}>
                                                        {route.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="w-1/6 text-center text-sm text-gray-500">
                                                    {device.last_package_at || "Нет данных"}
                                                </span>
                                            <Switch
                                                checked={device.enabled}
                                                onCheckedChange={(checked) => toggleDevice(device.id, checked)}
                                                className="w-8 h-5"
                                            />


                                            <div className="flex items-center space-x-2">
                                                <Textarea
                                                    defaultValue={device.name}
                                                    maxLength={30}
                                                    placeholder="name"
                                                    onChange={(e) =>
                                                        setDeviceNameEdits({
                                                            ...deviceNameEdits,
                                                            [device.id]: e.target.value
                                                        })
                                                    }
                                                    className="w-full"
                                                />
                                                <Button size="icon" className="inline-flex"
                                                        onClick={() => updateDeviceName(device.id)}>
                                                    <Save size={16}/>
                                                </Button>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Input
                                                    defaultValue={device.color}
                                                    maxLength={30}
                                                    placeholder="Color"
                                                    onChange={(e) =>
                                                        setDeviceColorEdits({
                                                            ...deviceColorEdits,
                                                            [device.id]: e.target.value
                                                        })
                                                    }
                                                    className="w-full"
                                                />
                                                <Button size="icon" className="inline-flex"
                                                        onClick={() => updateDeviceColor(device.id)}>
                                                    <Save size={16}/>
                                                </Button>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Textarea
                                                    defaultValue={device.icon}
                                                    placeholder="Icon"
                                                    onChange={(e) =>
                                                        setDeviceIconEdits({
                                                            ...deviceIconEdits,
                                                            [device.id]: e.target.value
                                                        })
                                                    }
                                                    className="w-full"
                                                />
                                                <Button size="icon" className="inline-flex"
                                                        onClick={() => updateDeviceIcon(device.id)}>
                                                    <Save size={16}/>
                                                </Button>
                                            </div>

                                        </div>
                                    ))}
                                </td>
                            </tr>
                        )}
                    </>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default AdminPanel;
